/**
 * Feature Usage Analytics — Type Definitions
 *
 * Organized by feature category. Each event includes a `category` property
 * so PostHog can filter/group natively without relying on naming prefixes.
 */

// ============================================================================
// Categories
// ============================================================================

export type FeatureCategory =
  | 'booking'
  | 'schedule'
  | 'catalog'
  | 'commerce'
  | 'sales'
  | 'billing'
  | 'clients'
  | 'team'
  | 'storefront'
  | 'referral'
  | 'analytics'
  | 'notifications'
  | 'settings'
  | 'ai'
  | 'onboarding'
  | 'setup'
  | 'auth'
  | 'ideas';

// ============================================================================
// Base
// ============================================================================

export interface BaseEventProperties {
  screen: string;
  category: FeatureCategory;
}

// ============================================================================
// Catalog (Services, Subscriptions, Unique Events, Courses, Downloadables)
// ============================================================================

export type CatalogAssetType =
  'service' | 'subscription' | 'unique_event' | 'online_course' | 'downloadable' | 'product';

export interface CatalogEventProperties extends BaseEventProperties {
  category: 'catalog';
  asset_type: CatalogAssetType;
  asset_id?: number;
  modality?: 'local' | 'virtual';
  price?: number;
  has_down_payment?: boolean;
  duration_minutes?: number;
  assigned_staff?: number[];
  labels?: string[];
  capacity?: number;
  ticket_types_count?: number;
  created_in_setup?: boolean;
  billing_cycle?: string;
  visibility?: boolean;
  status_change?: string;
  invitation_count?: number;
  /**
   * Desde dónde se asignó la categoría. Valida la hipótesis del rediseño: si el grueso
   * pasa por `card_menu` y `bulk`, el drag dejó de ser el cuello de botella.
   */
  source?: 'card_menu' | 'bulk' | 'organizer' | 'editor';
  /** Cuántos assets entraron en una asignación (1 desde la card, N desde selección). */
  assets_count?: number;
  /** `true` cuando el destino es "Sin categoría". */
  uncategorized?: boolean;
  /**
   * Packs y adicionales del servicio al guardar. Viven como PROPIEDADES del alta/edición
   * y no como eventos propios: `service_packs_configured` y `service_addons_configured`
   * se emitían 1:1 en el mismo handler y con el mismo `service_id` que
   * `service_created`/`service_edited` — 24,4k eventos/mes que no agregaban una sola
   * fila de información (sep/2026, recorte del free tier). `*_previous`/`*_delta` son
   * `undefined` en el alta, donde el previo es siempre 0.
   */
  packs_count?: number;
  packs_previous_count?: number;
  packs_delta?: number;
  pack_limits?: number[];
  addons_count?: number;
  addons_previous_count?: number;
  addons_delta?: number;
}

export const CATALOG_EVENTS = {
  // Services
  SERVICE_CREATED: 'service_created',
  SERVICE_EDITED: 'service_edited',
  SERVICE_DELETED: 'service_deleted',
  SERVICE_DUPLICATED: 'service_duplicated',
  SERVICE_SHARED: 'service_shared',
  SERVICES_REORDERED: 'services_reordered',
  SERVICE_CATEGORY_CREATED: 'service_category_created',
  SERVICE_CATEGORY_DELETED: 'service_category_deleted',
  SERVICE_CATEGORY_ASSIGNED: 'service_category_assigned',
  PRICES_ADJUSTED_GLOBALLY: 'prices_adjusted_globally',
  DOWNPAYMENT_ADJUSTED_GLOBALLY: 'downpayment_adjusted_globally',
  // Subscriptions
  SUBSCRIPTION_CREATED: 'subscription_created',
  SUBSCRIPTION_EDITED: 'subscription_edited',
  SUBSCRIPTION_DELETED: 'subscription_deleted',
  SUBSCRIPTIONS_REORDERED: 'subscriptions_reordered',
  // Unique Events
  UNIQUE_EVENT_CREATED: 'unique_event_created',
  UNIQUE_EVENT_EDITED: 'unique_event_edited',
  UNIQUE_EVENT_DELETED: 'unique_event_deleted',
  UNIQUE_EVENT_VISIBILITY_TOGGLED: 'unique_event_visibility_toggled',
  UNIQUE_EVENT_SHARED: 'unique_event_shared',
  TICKET_TYPE_ADDED: 'ticket_type_added',
  TICKET_TYPE_DELETED: 'ticket_type_deleted',
  INVITATIONS_SENT: 'invitations_sent',
  PARTICIPANTS_REPORT_DOWNLOADED: 'participants_report_downloaded',
  PARTICIPANT_CHECKED_IN: 'participant_checked_in',
  // Online Courses
  ONLINE_COURSE_CREATED: 'online_course_created',
  ONLINE_COURSE_EDITED: 'online_course_edited',
  ONLINE_COURSE_DELETED: 'online_course_deleted',
  ONLINE_COURSE_STATUS_CHANGED: 'online_course_status_changed',
  MODULE_ADDED: 'module_added',
  MODULE_DELETED: 'module_deleted',
  LESSON_ADDED: 'lesson_added',
  LESSON_DELETED: 'lesson_deleted',
  ONLINE_COURSES_REORDERED: 'online_courses_reordered',
  // Downloadables
  DOWNLOADABLE_CREATED: 'downloadable_created',
  DOWNLOADABLE_EDITED: 'downloadable_edited',
  DOWNLOADABLE_DELETED: 'downloadable_deleted',
  DOWNLOADABLES_REORDERED: 'downloadables_reordered',
  PRODUCTS_REORDERED: 'products_reordered',
  // Course invitations & participant contact
  COURSE_INVITATION_SENT: 'course_invitation_sent',
  EVENT_PARTICIPANTS_CONTACTED: 'event_participants_contacted',
  // Promoters
  PROMOTER_CREATED: 'promoter_created',
  PROMOTER_EDITED: 'promoter_edited',
  PROMOTER_DELETED: 'promoter_deleted',
  PROMOTER_ASSIGNED: 'promoter_assigned'
} as const;

// ============================================================================
// Booking
// ============================================================================

export interface BookingEventProperties extends BaseEventProperties {
  category: 'booking';
  payment_status?: 'pending' | 'confirmed' | 'failed';
  // partial / partial_settled = cobro parcial registrado contra el saldo
  // (partial_settled = ese cobro dejó el pago saldado).
  payment_type?: 'full' | 'down_payment' | 'partial' | 'partial_settled';
  total_amount?: number;
  client_id?: number;
  booking_date?: string;
  original_date?: string;
  asset_id?: number;
  reason?: string;
  has_refund?: boolean;
  has_assisted?: boolean;
  manual_channel?: string;
  /** Dónde se vio la ficha viva del cliente (live_card_viewed). */
  surface?: 'action_dialog' | 'hover' | 'profile' | 'drawer';
  /** Qué datita eligió la ficha viva (live_card_viewed): la key de `pickDatita`
   *  ('vencido', 'segunda_visita', 'notes', …). `null` = ficha sin datita. */
  datita_key?: string | null;
  /** Keys de los chips pintados en la ficha viva (live_card_viewed), en el
   *  orden en que se mostraron ('faltas', 'cancelaciones', 'horario_habitual',
   *  'gift_card', …). `[]` = ninguno. */
  chips?: string[];
  /** Cómo se cargó la nota (voice_note_saved, booking_notes_updated). */
  note_input?: 'voice' | 'text';
  /** POR QUÉ PUERTA entró la nota (booking_notes_updated). `turn_close` es el
   *  cierre del turno; `editor` es el campo de notas de siempre, que ya venía
   *  usándose. Sin esto los dos se mezclan en un solo número y el histórico
   *  tapa la adopción de la feature nueva. */
  note_origin?: 'turn_close' | 'editor';
  /** El cierre del turno pidió la nota (turn_close_prompt_shown): false = el turno
   *  ya tenía una y solo quedó el CTA de re-agendar. */
  note_prompt?: boolean;
  /** El cierre del turno ofreció re-agendar por WhatsApp (turn_close_prompt_shown). */
  rebook_offered?: boolean;
  /** Intento número N de cargar la nota en el mismo turno (turn_close_note_started).
   *  `attempt: 1` aísla el primer intento; >1 es alguien reintentando tras un tropiezo. */
  attempt?: number;
  /** Por qué el intento no terminó en nota (turn_close_note_dropped). Separa
   *  "no le interesó" (`dismissed`, `discarded`) de "se trabó" (el resto): sin esto,
   *  un cierre sin nota es indistinguible de uno que falló. */
  drop_reason?:
    | 'mic_denied'
    | 'mic_error'
    | 'empty_recording'
    | 'discarded'
    | 'transcribe_failed'
    | 'upload_error'
    | 'dismissed';
}

export const BOOKING_EVENTS = {
  BOOKING_CREATED: 'booking_created_manually',
  BOOKING_CANCELLED: 'booking_cancelled',
  BOOKING_RESCHEDULED: 'booking_rescheduled',
  BOOKING_PAYMENT_CONFIRMED: 'booking_payment_confirmed',
  BOOKING_ATTENDANCE_CONFIRMED: 'booking_attendance_confirmed',
  /** La ficha viva del cliente se mostró CON data (banda del detalle o hover de agenda). */
  LIVE_CARD_VIEWED: 'live_card_viewed',
  /** El cierre del turno guardó una nota (por voz o tipeada). */
  VOICE_NOTE_SAVED: 'voice_note_saved',
  /** El CTA "ofrecer re-agendar" del cierre del turno se clickeó. */
  REBOOK_CTA_CLICKED: 'rebook_cta_clicked',
  /** El cierre del turno se desplegó al marcar asistencia. Es el DENOMINADOR de
   *  `voice_note_saved`: el prompt solo aparece si el turno no tenía nota, así que
   *  medir contra `booking_attendance_confirmed` mezcla turnos que nunca lo vieron. */
  TURN_CLOSE_PROMPT_SHOWN: 'turn_close_prompt_shown',
  /** Alguien EMPEZÓ a cargar la nota del cierre: tocó "Grabar nota" (`note_input:
   *  'voice'`) o "Escribir" (`'text'`). Es el escalón que faltaba entre "se mostró"
   *  y "se guardó": sin él, los cierres sin nota mezclan a quien no lo miró con
   *  quien lo intentó y no pudo. Solo cuenta la intención deliberada — los desvíos
   *  automáticos al editor (micrófono bloqueado, Whisper caído) viajan como
   *  `turn_close_note_dropped`. */
  TURN_CLOSE_NOTE_STARTED: 'turn_close_note_started',
  /** El intento no terminó en nota. `drop_reason` dice por qué. */
  TURN_CLOSE_NOTE_DROPPED: 'turn_close_note_dropped',
  BOOKING_NO_SHOW: 'booking_no_show_marked',
  /** Se guardó una nota de turno, POR CUALQUIER PUERTA. `note_origin` separa el
   *  cierre del turno del editor de siempre — es la única señal que lo hace: en la
   *  DB `bei.notes_updated_at` no distingue quién la escribió. Solo se emite con
   *  texto real: vaciar la nota es un borrado, no un uso. */
  BOOKING_NOTES_UPDATED: 'booking_notes_updated'
} as const;

// ============================================================================
// Schedule (Working Hours, Blocked Hours, Google Calendar)
// ============================================================================

export interface ScheduleEventProperties extends BaseEventProperties {
  category: 'schedule';
  days_configured?: string[];
  schedule_template?: 'morning' | 'afternoon' | 'full_day' | 'custom';
  start_date?: string;
  end_date?: string;
  view_type?: string;
}

export const SCHEDULE_EVENTS = {
  WORKING_HOURS_UPDATED: 'working_hours_updated',
  BLOCKED_HOURS_CREATED: 'blocked_hours_created',
  BLOCKED_HOURS_EDITED: 'blocked_hours_edited',
  SCHEDULE_COPIED: 'schedule_copied',
  GOOGLE_CALENDAR_CONNECTED: 'google_calendar_connected',
  GOOGLE_CALENDAR_DISCONNECTED: 'google_calendar_disconnected'
} as const;

// ============================================================================
// Sales (Payment Gateways, Discounts)
// ============================================================================

export interface SalesEventProperties extends BaseEventProperties {
  category: 'sales';
  gateway?: 'mercado_pago' | 'stripe' | 'bank_transfer' | 'cash';
  method?: string;
  discount_type?: 'percentage' | 'fixed';
  discount_value?: number;
  usage_limit?: number;
  code?: string;
  coupon_id?: number;
  applies_to_types?: string[];
  assets_count?: number;
  has_expiration?: boolean;
  has_usage_limit?: boolean;
  purchase_days_count?: number;
  shift_days_count?: number;
  refund_amount?: number;
  // Venta manual de productos sueltos — flow "Ingresar venta" (POS)
  entry_point?: 'caja' | 'catalogo' | 'actividad' | 'agenda' | 'finanzas';
  items_count?: number;
  units_count?: number;
  total_amount?: number;
  /** Neto cobrado (total menos descuento, más aumento). */
  amount?: number;
  payment_method?: string;
  has_professional?: boolean;
  has_client?: boolean;
  discount_amount?: number;
  surcharge_amount?: number;
  /** Alguna línea se vendió por encima del stock (el stock se clava en 0). */
  oversold?: boolean;
  /** Anulación: monto revertido de la caja. */
  reversed?: number;
  /** Anulación: quedó monto sin revertir (arqueo cerrado → requiere devolución manual). */
  survived?: boolean;
}

export const SALES_EVENTS = {
  GATEWAY_CONNECTED: 'payment_gateway_connected',
  PAYMENT_METHOD_ENABLED: 'payment_method_enabled',
  PAYMENT_METHOD_DISABLED: 'payment_method_disabled',
  DISCOUNT_CREATED: 'discount_code_created',
  DISCOUNT_EDITED: 'discount_code_edited',
  DISCOUNT_DELETED: 'discount_code_deleted',
  DISCOUNT_CODE_COPIED: 'discount_code_copied',
  REFUND_PROCESSED: 'refund_processed',
  // Flow "Ingresar venta" (venta manual de productos sueltos)
  PRODUCT_SALE_REGISTERED: 'product_sale_registered',
  PRODUCT_SALE_ANNULLED: 'product_sale_annulled'
} as const;

// ============================================================================
// Billing
// ============================================================================

export interface BillingEventProperties extends BaseEventProperties {
  category: 'billing';
  current_plan?: string;
  new_plan?: string;
  plan_price?: number;
  currency?: string;
  addon_type?: string;
  addon_quantity?: number;
  addon_price?: number;
  reason?: string;
  step?: string;
  details?: string;
  from?: string;
}

export const BILLING_EVENTS = {
  PLAN_VIEWED: 'billing_plan_viewed',
  PLAN_UPGRADED: 'billing_plan_upgraded',
  PLAN_SUBSCRIBED: 'billing_plan_subscribed',
  ADDON_PURCHASED: 'add_on_purchased',
  CHURN_REQUESTED: 'churn_requested',
  CHURN_CANCELLED: 'churn_cancelled',
  CHURN_FUNNEL_STEP: 'churn_funnel_step'
} as const;

// ============================================================================
// Clients
// ============================================================================

export interface ClientEventProperties extends BaseEventProperties {
  category: 'clients';
  client_id?: number;
  has_photo?: boolean;
  source?: string;
  tag_id?: number;
  tag_name?: string;
  note_type?: 'general' | 'medical' | 'preference' | 'other';
  contact_method?: 'whatsapp' | 'email';
  duplicate_client_id?: number;
}

export const CLIENT_EVENTS = {
  CLIENT_ADDED: 'client_added',
  CLIENT_EDITED: 'client_edited',
  CLIENT_DELETED: 'client_deleted',
  CLIENT_CONTACTED: 'client_contacted',
  CLIENT_TAG_ASSIGNED: 'client_tag_assigned',
  CLIENT_TAG_REMOVED: 'client_tag_removed',
  CLIENT_TAG_CREATED: 'client_tag_created',
  CLIENT_TAG_DELETED: 'client_tag_deleted',
  CLIENT_NOTE_ADDED: 'client_note_added',
  CLIENT_REPORT_DOWNLOADED: 'client_report_downloaded',
  CLIENTS_MERGED: 'clients_merged'
} as const;

// ============================================================================
// Team
// ============================================================================

export interface TeamEventProperties extends BaseEventProperties {
  category: 'team';
  staff_id?: number;
  assigned_services_count?: number;
  permissions?: string[];
  status?: 'active' | 'inactive';
}

export const TEAM_EVENTS = {
  STAFF_ADDED: 'staff_added',
  STAFF_EDITED: 'staff_edited',
  STAFF_DELETED: 'staff_deleted',
  STAFF_PERMISSIONS_CHANGED: 'staff_permissions_changed',
  STAFF_DEACTIVATED: 'staff_deactivated',
  STAFF_REACTIVATED: 'staff_reactivated',
  STAFF_INVITATION_CONFIRMED: 'staff_invitation_confirmed',
  STAFF_PAYMENT_RECORDED: 'staff_payment_recorded',
  STAFF_BALANCE_ADJUSTED: 'staff_balance_adjusted'
} as const;

// ============================================================================
// Storefront
// ============================================================================

export type StorefrontSection = 'header' | 'about' | 'services' | 'footer' | 'design' | 'links' | 'gallery';
export type ShareMethod = 'whatsapp' | 'copy_link' | 'qr_code' | 'download_qr';

export interface StorefrontEventProperties extends BaseEventProperties {
  category: 'storefront';
  section?: StorefrontSection;
  template?: string;
  share_method?: ShareMethod;
}

export const STOREFRONT_EVENTS = {
  SECTION_EDITED: 'storefront_section_edited',
  TEMPLATE_CHANGED: 'storefront_template_changed',
  PUBLISHED: 'storefront_published',
  SHARED: 'storefront_shared',
  PREVIEWED: 'storefront_previewed',
  GALLERY_IMAGE_UPLOADED: 'gallery_image_uploaded',
  GALLERY_IMAGE_DELETED: 'gallery_image_deleted',
  GALLERY_PUBLISHED: 'gallery_published',
  QR_CODE_DOWNLOADED: 'storefront_qr_code_downloaded'
} as const;

// ============================================================================
// Referral
// ============================================================================

export interface ReferralEventProperties extends BaseEventProperties {
  category: 'referral';
  share_method?: string;
}

export const REFERRAL_EVENTS = {
  LINK_SHARED: 'referral_link_shared'
} as const;

// ============================================================================
// Analytics
// ============================================================================

export interface AnalyticsEventProperties extends BaseEventProperties {
  category: 'analytics';
  report_type?: 'clients' | 'transactions' | 'unique_event' | 'bookings' | 'activities';
  date_range_days?: number;
}

export const ANALYTICS_EVENTS = {
  DASHBOARD_VIEWED: 'analytics_dashboard_viewed',
  REPORT_DOWNLOADED: 'report_downloaded'
} as const;

// ============================================================================
// Notifications
// ============================================================================

export interface NotificationEventProperties extends BaseEventProperties {
  category: 'notifications';
  notification_type?: string;
}

export const NOTIFICATION_EVENTS = {
  CENTER_VIEWED: 'notification_center_viewed',
  ACTION_CLICKED: 'notification_action_clicked'
} as const;

// ============================================================================
// Settings
// ============================================================================

export interface SettingsEventProperties extends BaseEventProperties {
  category: 'settings';
  integration?: string;
}

export const SETTINGS_EVENTS = {
  PIN_ENABLED: 'settings_pin_enabled',
  PROFILE_EDITED: 'settings_profile_edited',
  INTEGRATION_CONNECTED: 'settings_integration_connected',
  INTEGRATION_DISCONNECTED: 'settings_integration_disconnected',
  SUPPORT_MESSAGE_SENT: 'support_message_sent'
} as const;

// ============================================================================
// AI
// ============================================================================

export interface AIEventProperties extends BaseEventProperties {
  category: 'ai';
  content_type?: string;
  mode?: 'generate' | 'enhance';
  enhance_action?: 'improve' | 'expand' | 'summarize' | 'persuade';
  tone?: 'professional' | 'warm' | 'premium' | 'dynamic';
  length?: 'short' | 'medium' | 'detailed';
  had_existing_content?: boolean;
}

export const AI_EVENTS = {
  CONTENT_GENERATED: 'ai_content_generated',
  CONTENT_APPLIED: 'ai_content_applied',
  CONTENT_DISCARDED: 'ai_content_discarded',
  INSIGHTS_GENERATED: 'ai_insights_generated'
} as const;

// ============================================================================
// Onboarding / Setup  →  ver `types/analytics/activation.ts`
//
// La taxonomía del camino signup → vendor operativo se rehizo el 14/jul/2026. Los
// eventos numerados (`onboarding_step_N_*`, `setup_1_step_N_*`, `setup_2_step_N_*`)
// estaban keyeados al NÚMERO DE SLOT de la pantalla: cada vez que un paso se movía o
// se daba de baja, la serie se rompía y los números mentían en silencio. Se
// reemplazaron por UN evento con el paso como property. NO los revivas acá.
//
// `onboarding_plan_saved` confirma la escritura del plan. `free_trial_started`
// sólo se emite cuando Cyclone recibió is_free_trial=true; el estado durable de
// Cyclone sigue siendo la fuente de verdad del embudo.
// ============================================================================

export interface OnboardingEventProperties extends BaseEventProperties {
  category: 'onboarding';
  plan_id?: number;
  is_free_trial?: boolean;
  staff_count?: number;
}

export const ONBOARDING_EVENTS = {
  /** Cyclone confirmó el POST me/vendor/plans, sin inferir estado de suscripción. */
  PLAN_SAVED: 'onboarding_plan_saved',
  /** Sólo cuando la mutación confirmada inició efectivamente una prueba. */
  FREE_TRIAL_STARTED: 'free_trial_started'
} as const;

// ============================================================================
// Auth
// ============================================================================

export interface AuthEventProperties extends BaseEventProperties {
  category: 'auth';
  method?: 'email' | 'google';
  source?: 'nightwing' | 'flash' | 'marketplace' | 'direct';
  is_vendor?: boolean;
  is_new_user?: boolean;
  is_returning_user?: boolean;
  redirect_target?: string;
  error_type?: string;
  error_message?: string;
  // Intención del flujo persistida ANTES del redirect a Auth0 (sessionStorage):
  // permite atribuir fallos del callback a signup vs login, que el callback en
  // sí no puede distinguir. 'unknown' = no había intent (expiró o storage falló).
  auth_flow_intent?: 'signup' | 'login' | 'unknown';
  // En fallos del callback: si la URL traía tokens de Auth0 (volvió con hash pero
  // la validación de state/nonce falló) vs llegó sin nada (redirect roto antes).
  had_auth_tokens?: boolean;
  // Recovery del callback (jul/2026): si se intentó el retry con prompt=none ante
  // un state mismatch, y si el flujo terminó recuperado. En LOGIN/SIGNUP_COMPLETED,
  // recovered_from_callback_error marca los éxitos que llegaron vía ese retry.
  recovery_attempted?: boolean;
  recovered?: boolean;
  // Desenlace del fallo del callback. 'retrying' = breadcrumb emitido ANTES del
  // redirect de rescate (el usuario suele volver bien; además se pierde seguido en
  // el unload). 'bounced' = terminó en /login de verdad. Sin esto los dos casos
  // emitían properties idénticas y el conteo de fallas estaba inflado ~4x.
  // Para medir DAÑO real filtrar por recovery_outcome='bounced'.
  recovery_outcome?: 'retrying' | 'bounced';
  recovered_from_callback_error?: boolean;
  // En CALLBACK_NO_HASH: si la sesión seguía viva al caer en el /callback viejo
  // (true = lo mandamos de vuelta a la app en vez de deslogearlo).
  authenticated?: boolean;
  // En CALLBACK_NO_HASH: por qué no se parseó. 'no_hash' = el callback montó sin
  // hash de verdad (entrada vieja del historial, el caso benigno). 'reentry' = el
  // hash existe pero otro montaje de este mismo documento ya lo parseó — no se
  // puede repetir, el state de auth0-js es one-shot. Ver auth/callbackHash.ts.
  reason?: 'no_hash' | 'reentry';
  // ── Diagnóstico del arranque de sesión (ago/2026, ver auth/sessionDiagnostics.ts).
  // Foto del storage tomada ANTES de llamar a Auth0: auth0-spa-js limpia su propio
  // cache cuando el silent auth falla, así que después ya no se puede distinguir
  // "murió la cookie SSO y el localStorage está intacto" (→ prender
  // allow_offline_access lo arregla) de "WebKit barrió el localStorage entero"
  // (→ un refresh token se borraría también y NO arregla nada).
  error_name?: string;
  online?: boolean;
  /** `visibility` a secas NO: ya está tipada como booleana en el proyecto. */
  visibility_state?: string;
  has_auth0_cache?: boolean;
  has_refresh_token?: boolean;
  refresh_token_family_age_days?: number | null;
  refresh_token_age_known?: boolean;
  session_establishment?: 'pkce' | 'legacy';
  has_is_auth_cookie?: boolean;
  has_own_keys?: boolean;
  ls_key_count?: number;
  canary_age_days?: number;
  storage_available?: boolean;
  had_prior_session?: boolean;
  /** El rebote a /login se ejecutó de verdad (vs. ruta pública que lo tolera). */
  bounced?: boolean;
  /**
   * Fracción emitida de este slice, para poder extrapolar. Las fallas SIN
   * evidencia de sesión previa se muestrean (un vendor con el storage barrido es
   * indistinguible de un anónimo desde el cliente); las que sí tienen evidencia
   * van al 100% y salen con 1.
   */
  sample_rate?: number;
  session_duration_minutes?: number;
  claimed_business_id?: string;
  claimed_business_name?: string;
}

export const AUTH_EVENTS = {
  SIGNUP_STARTED: 'auth_signup_started',
  // Cuenta creada en Auth0 (dispara en NUESTRO dominio, antes del redirect al
  // callback). SIGNUP_COMPLETED dispara recién en /callback — si el redirect o
  // el stitching cross-subdominio fallan, este evento igual cuenta el alta.
  SIGNUP_ACCOUNT_CREATED: 'auth_signup_account_created',
  SIGNUP_COMPLETED: 'auth_signup_completed',
  SIGNUP_FAILED: 'auth_signup_failed',
  // El pre-check de users/verify frenó el alta porque el email ya tiene cuenta.
  // Mismo nombre que en nightwing (taxonomía compartida, property source distingue).
  SIGNUP_BLOCKED_EXISTING_EMAIL: 'auth_signup_blocked_existing_email',
  LOGIN_STARTED: 'auth_login_started',
  LOGIN_COMPLETED: 'auth_login_completed',
  LOGIN_FAILED: 'auth_login_failed',
  // Credenciales inválidas en el form (contraseña/código incorrecto) — es
  // comportamiento de usuario, NO una falla técnica. Separado de LOGIN_FAILED
  // para que ese evento mida solo roturas reales del flujo (callback, chunks, red).
  CREDENTIALS_REJECTED: 'auth_credentials_rejected',
  // /callback montado SIN hash: no es una respuesta de Auth0 sino una entrada
  // vieja del historial (el hash se limpia con replaceState al consumirlo), a la
  // que se llega volviendo atrás. Antes caía en LOGIN_FAILED y deslogueaba a
  // ~20-30 personas/día. La property authenticated dice si la sesión seguía viva.
  CALLBACK_NO_HASH: 'auth_callback_no_hash',
  // Arranque de la app: `getAccessTokenSilently()` falló y el Router rebota a
  // /login. Hasta ago/2026 ese camino no emitía NADA (catch pelado en
  // Router.tsx), y es la salida más probable del sistema — por eso el
  // "me tengo que loguear cada vez" de Safari/iOS llegó por Slack y nunca por
  // un dashboard. Solo se emite si había evidencia de sesión previa en el
  // dispositivo: un visitante anónimo rebotando a /login no es un bug.
  BOOTSTRAP_FAILED: 'auth_bootstrap_failed',
  // Contraparte sana del anterior, muestreada al 5% (ver SAMPLED_EVENTS). Da el
  // contraste de storage: cómo se ve el localStorage de alguien que SÍ entró.
  BOOTSTRAP_OK: 'auth_bootstrap_ok',
  LOGOUT: 'auth_logout',
  PASSWORD_RESET_REQUESTED: 'auth_password_reset_requested',
  CLAIM_BUSINESS_CONVERTED: 'auth_claim_business_converted'
} as const;

// ============================================================================
// Ideas (Buzón de Ideas / feedback loop)
// ============================================================================

export interface IdeasEventProperties extends BaseEventProperties {
  category: 'ideas';
  origin?: 'voice' | 'text';
  has_audio?: boolean;
  char_count?: number;
}

export const IDEAS_EVENTS = {
  IDEA_BOX_VIEWED: 'idea_box_viewed',
  IDEA_SUBMITTED: 'idea_submitted'
} as const;

// ============================================================================
// Union types
// ============================================================================
